/**
 * Monaco Editor Themes for Envelop
 *
 * Provides light and dark themes that sync with the application theme.
 */

import type * as Monaco from 'monaco-editor';

/**
 * IDF theme colors for dark mode
 */
export const idfDarkThemeRules: {
  token: string;
  foreground?: string;
  fontStyle?: string;
}[] = [
  { token: 'comment', foreground: '6A9955' },
  { token: 'comment.doc', foreground: '6A9955', fontStyle: 'italic' },
  { token: 'type', foreground: '4EC9B0' },
  { token: 'type.identifier', foreground: '4EC9B0', fontStyle: 'bold' },
  { token: 'keyword', foreground: '569CD6' },
  { token: 'number', foreground: 'B5CEA8' },
  { token: 'number.float', foreground: 'B5CEA8' },
  { token: 'string', foreground: 'CE9178' },
  { token: 'string.date', foreground: 'DCDCAA' },
  { token: 'delimiter', foreground: 'D4D4D4' },
  { token: 'delimiter.semicolon', foreground: 'D4D4D4', fontStyle: 'bold' },
  { token: 'constant', foreground: '4FC1FF' },
];

/**
 * IDF theme colors for light mode
 */
export const idfLightThemeRules: {
  token: string;
  foreground?: string;
  fontStyle?: string;
}[] = [
  { token: 'comment', foreground: '008000' },
  { token: 'comment.doc', foreground: '008000', fontStyle: 'italic' },
  { token: 'type', foreground: '267F99' },
  { token: 'type.identifier', foreground: '267F99', fontStyle: 'bold' },
  { token: 'keyword', foreground: '0000FF' },
  { token: 'number', foreground: '098658' },
  { token: 'number.float', foreground: '098658' },
  { token: 'string', foreground: 'A31515' },
  { token: 'string.date', foreground: '795E26' },
  { token: 'delimiter', foreground: '000000' },
  { token: 'delimiter.semicolon', foreground: '000000', fontStyle: 'bold' },
  { token: 'constant', foreground: '0070C1' },
];

/**
 * Dark theme definition matching Envelop's dark mode
 */
export const envelopDarkTheme: Monaco.editor.IStandaloneThemeData = {
  base: 'vs-dark',
  inherit: true,
  rules: idfDarkThemeRules,
  colors: {
    'editor.background': '#0a0f1a', // Matches --background in dark mode
    'editor.foreground': '#e2e8f0', // Matches --foreground in dark mode
    'editor.lineHighlightBackground': '#1a2035',
    'editor.selectionBackground': '#264f78',
    'editorLineNumber.foreground': '#6b7280',
    'editorCursor.foreground': '#e2e8f0',
    'editor.inactiveSelectionBackground': '#1e3a5f',
    'editorIndentGuide.background': '#2d3748',
    'editorIndentGuide.activeBackground': '#4a5568',
    'editorWidget.background': '#1a2035',
    'editorWidget.border': '#2d3748',
    'editorSuggestWidget.background': '#1a2035',
    'editorSuggestWidget.border': '#2d3748',
    'editorSuggestWidget.selectedBackground': '#2d4a6f',
    'editorHoverWidget.background': '#1a2035',
    'editorHoverWidget.border': '#2d3748',
  },
};

/**
 * Light theme definition matching Envelop's light mode
 */
export const envelopLightTheme: Monaco.editor.IStandaloneThemeData = {
  base: 'vs',
  inherit: true,
  rules: idfLightThemeRules,
  colors: {
    'editor.background': '#ffffff', // Matches --background in light mode
    'editor.foreground': '#1a202c', // Matches --foreground in light mode
    'editor.lineHighlightBackground': '#f7fafc',
    'editor.selectionBackground': '#c8e1ff',
    'editorLineNumber.foreground': '#a0aec0',
    'editorCursor.foreground': '#1a202c',
    'editor.inactiveSelectionBackground': '#e2e8f0',
    'editorIndentGuide.background': '#e2e8f0',
    'editorIndentGuide.activeBackground': '#cbd5e0',
    'editorWidget.background': '#ffffff',
    'editorWidget.border': '#e2e8f0',
    'editorSuggestWidget.background': '#ffffff',
    'editorSuggestWidget.border': '#e2e8f0',
    'editorSuggestWidget.selectedBackground': '#e2e8f0',
    'editorHoverWidget.background': '#ffffff',
    'editorHoverWidget.border': '#e2e8f0',
  },
};

/**
 * Register both light and dark themes with Monaco
 */
export function registerEnvelopThemes(monaco: typeof Monaco): void {
  monaco.editor.defineTheme('envelop-dark', envelopDarkTheme);
  monaco.editor.defineTheme('envelop-light', envelopLightTheme);
}

/**
 * Get the Monaco theme name based on the resolved app theme
 */
export function getMonacoTheme(resolvedTheme: 'light' | 'dark'): string {
  return resolvedTheme === 'dark' ? 'envelop-dark' : 'envelop-light';
}
